import {
  BlogPostEntry,
  BlogPostSkeleton,
  TagEntry
} from '@/utils/contentful/types';
import { Asset, Entry } from 'contentful';
import Image from 'next/image';
import React from 'react';
import articleBg from '@/public/lander_hero.jpg';
import Title from '@/components/ui/Title';
import SubTitle from '@/components/ui/SubTitle';
import Container from '@/components/ui/Container';
import Link from 'next/link';
import fallbackImage from '@/public/default-fallback-image.png'; // Fallback image in case the post image is not available
import { Dictionary, Locales } from '@/app/[lang]/dictonaries';
import Button from '../../Button';
import { cn } from '@/lib/utils';
import { Tag } from 'lucide-react';

type BlogPreviewProps = {
  post: BlogPostEntry;
  lang: Locales;
  dict: Dictionary;
};

export default function BlogPreview({ post, lang, dict }: BlogPreviewProps) {
  const image = post.fields.image as Asset | undefined;
  const imageUrl = image?.fields.file?.url
    ? `https:${image.fields.file.url}`
    : fallbackImage.src;

  return (
    <article className="">
      <Container className="w-full">
        <Link
          href={`/blog/${post.fields.slug}`}
          className="block relative rounded-3xl overflow-hidden"
        >
          <div className="flex flex-col md:flex-row relative z-10 w-full h-full backdrop-blur-2xl bg-black/30 text-white">
            {imageUrl && (
              <div className="w-full md:flex-[0_0_45%] relative h-40 md:h-auto">
                <Image
                  src={imageUrl}
                  alt={(image?.fields.title as string) || 'fallback image'}
                  fill
                  className="object-cover"
                />
              </div>
            )}
            <div className="flex flex-col justify-between basis-auto px-5 py-5 md:px-7">
              <div>
                <Title
                  color="light"
                  className="text-xl md:text-2xl lg:text-3xl"
                >
                  {post.fields.title as string}
                </Title>
                <SubTitle
                  color="light"
                  className="text-sm leading-6 font-normal mt-2"
                >
                  {post.fields.description as string}
                </SubTitle>
              </div>
              <div className="flex flex-col gap-5 justify-between mt-5">
                {post.fields.tags && post.fields.tags.length > 0 && (
                  <div className='flex flex-wrap items-center gap-2'>
                    <Tag />
                    {post.fields.tags.slice(0, 2).map((tag) => {
                      const tagEntry = tag as TagEntry;

                      console.log(tagEntry, 'tagEntry');

                      if (!tagEntry || !tagEntry.fields) {
                        return null;
                      }

                      return (
                        <span
                          key={tagEntry.sys.id}
                          style={{ backgroundColor: tagEntry.fields.color || '#e0e0e0', color: tagEntry.fields.textColor || '#000' }}
                          className={cn(
                            'inline-block px-3 py-1 rounded-full text-xs font-semibold'
                          )}
                        >
                          {tagEntry.fields.tagName}
                        </span>
                      );
                    })}
                    {post.fields.tags.length > 2 && (
                      <span className="text-xs">
                        +{post.fields.tags.length - 2} more
                      </span>
                    )}
                  </div>
                )}
                <div className="flex items-center flex-wrap-reverse gap-5 justify-between">
                  <Link href={`/blog/${post.fields.slug}`}>
                    <Button
                      variant={'slim'}
                      className={
                        'lg:text-lg lg:h-12 bg-white hover:bg-gray-200 text-[#213735]'
                      }
                    >
                      {dict.pages.blog.post.read_more}
                    </Button>
                  </Link>
                  <span className="text-sm text-gray-300">
                    {new Date(post.sys.createdAt).toLocaleDateString(lang, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <Image
            src={articleBg}
            alt="Article Background"
            fill
            className="object-cover"
          />
        </Link>
      </Container>
    </article>
  );
}
